import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import NangoConnectionsApi from '@/infrastructure/api/nango-connections'

const APP_AUTOMATION_TOKEN_KEY = ['shopifyAppAutomationToken']

export function appAutomationTokenKey(ecommerceId?: string) {
  return [...APP_AUTOMATION_TOKEN_KEY, ecommerceId]
}

export function useShopifyAppAutomationToken({
  enabled,
  ecommerceId
}: {
  enabled: boolean
  ecommerceId?: string
}) {
  const query = useQuery({
    queryKey: appAutomationTokenKey(ecommerceId),
    queryFn: ({ signal }) =>
      NangoConnectionsApi.findAppAutomationToken(ecommerceId ?? '', signal),
    enabled: enabled && !!ecommerceId
  })

  return {
    token: query.data,
    loadingToken: query.isLoading,
    tokenError: query.isError,
    reloadToken: query.refetch
  }
}

export function useSaveShopifyAppAutomationToken() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      ecommerceId,
      token,
      expiresAt
    }: {
      ecommerceId: string
      token: string
      expiresAt: Date
    }) =>
      NangoConnectionsApi.saveAppAutomationToken(ecommerceId, token, expiresAt),
    onSuccess: async (saved, { ecommerceId }) => {
      const queryKey = appAutomationTokenKey(ecommerceId)
      // A read that started before the save would land after it with the old token.
      await queryClient.cancelQueries({ queryKey })
      queryClient.setQueryData(queryKey, saved)
    },
    // The server saves the stores' rows before Nango, so a failed save may still change what it serves.
    onError: (_, { ecommerceId }) =>
      queryClient.invalidateQueries({
        queryKey: appAutomationTokenKey(ecommerceId)
      })
  })

  return {
    saveToken: mutation.mutateAsync,
    savingToken: mutation.isPending
  }
}
